import { env } from '@shared/config';

import { maskSensitive, maskText } from './mask';

export const LOG_LEVEL = { debug: 10, info: 20, warn: 30, error: 40, silent: 100 } as const;
export type LogLevel = Exclude<keyof typeof LOG_LEVEL, 'silent'>;
export type LogThreshold = keyof typeof LOG_LEVEL;

export interface LogEntry {
  level: LogLevel;
  message: string;
  /** ISO 8601 UTC */
  timestamp: string;
  env: string;
  traceId?: string;
  context?: Record<string, unknown>;
}

export type LogSink = (entry: LogEntry) => void;

export interface LogContext extends Record<string, unknown> {
  traceId?: string;
}

const consoleSink: LogSink = (entry) => {
  // 콘솔을 쓰는 곳은 이 파일 하나다(다른 곳은 lint 가 막는다).
  // eslint-disable-next-line no-console
  console[entry.level](`[${entry.level}] ${entry.message}`, entry);
};

export function createLogger(options: { threshold: LogThreshold; env: string; sinks: LogSink[] }) {
  const sinks = new Set<LogSink>(options.sinks);

  function write(level: LogLevel, message: string, context?: LogContext) {
    if (LOG_LEVEL[level] < LOG_LEVEL[options.threshold]) return;
    try {
      const { traceId, ...rest } = context ?? {};
      const entry: LogEntry = {
        level,
        message: maskText(message),
        timestamp: new Date().toISOString(),
        env: options.env,
        ...(traceId ? { traceId } : {}),
        ...(Object.keys(rest).length > 0
          ? { context: maskSensitive(rest) as Record<string, unknown> }
          : {}),
      };
      for (const sink of sinks) {
        try {
          sink(entry);
        } catch {
          // 한 sink 의 실패가 호출부나 다른 sink 로 번지지 않게 한다.
        }
      }
    } catch {
      // 로깅은 절대 throw 하지 않는다.
    }
  }

  return {
    debug: (message: string, context?: LogContext) => write('debug', message, context),
    info: (message: string, context?: LogContext) => write('info', message, context),
    warn: (message: string, context?: LogContext) => write('warn', message, context),
    error: (message: string, context?: LogContext) => write('error', message, context),
    /** sink 를 더한다. 돌려준 함수를 부르면 뺀다. */
    addSink(sink: LogSink) {
      sinks.add(sink);
      return () => {
        sinks.delete(sink);
      };
    },
  };
}

export type Logger = ReturnType<typeof createLogger>;

export const logger: Logger = createLogger({
  threshold: env.logLevel,
  env: env.appEnv,
  sinks: [consoleSink],
});
